import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

@Injectable({
    providedIn: 'root'
})
export class CommonService {

    private renderer: Renderer2;
    urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
    onlyNumericAndAlphabelRegex = /^[a-zA-Z0-9]+$/;
    onlyNumericRegex = /^[0-9]+$/;

    constructor(private rendererFactory: RendererFactory2) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    matchingPasswords(passwordKey: string, confirmPasswordKey: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const password = control.get(passwordKey);
            const confirmPassword = control.get(confirmPasswordKey);

            return password?.value === confirmPassword?.value ? null : { notmatched: true };
        };
    }

    noWhitespaceValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return (control.value || '').trim().length ? null : { 'whitespace': true };
        };
    }

    /**
     * Converts various string/number/boolean values to proper boolean.
     * Handles edge cases like '0', '1', 'true', 'false', 0, 1, true, false
     * @param value The value to convert to boolean
     * @returns boolean - true for truthy values ('1', 1, 'true', true), false otherwise
     */
    toBooleanSafe(value: string | number | boolean | null | undefined): boolean {
        // Handle null/undefined
        if (value === null || value === undefined) {
            return false;
        }

        // Handle boolean type
        if (typeof value === 'boolean') {
            return value;
        }

        // Handle number type
        if (typeof value === 'number') {
            return value !== 0;
        }

        // Handle string type
        if (typeof value === 'string') {
            const trimmed = value.trim().toLowerCase();
            return trimmed === 'true' || trimmed === '1';
        }

        return false;
    }

    initializeAcceptBlueTokenizationURL(tokenizationAPIURL: string) {

        //Delete existing object so it doesn't throw error (window.HostedTokenization si already defined.)
        delete (window as any)?.HostedTokenization;

        const scriptElement = document.getElementById('tokenization-api-url');
        scriptElement?.parentElement?.removeChild(scriptElement);

        const script = this.renderer.createElement('script');
        script.src = tokenizationAPIURL;
        script.id = 'tokenization-api-url';
        this.renderer.appendChild(document.head, script);

    }

    initializeAcceptBluePaymentIframe(tokenizationSourceKey: string): any {
        return new Promise((resolve, reject) => {

            setTimeout(() => {
                const hostedTokenization = new (window as any).HostedTokenization(tokenizationSourceKey);
                const cardForm = hostedTokenization.create('card-form');

                cardForm.mount('#trigger-card-payment');
                cardForm.setStyles({
                    container: 'display: grid; width:100%',
                    card: "width: 100%; font-size: 1rem; color: #334155;background: #ffffff;padding: 0.75rem 0.75rem;border: 1px solid #cbd5e1;border-radius: 6px;margin-bottom: 1rem;    box-sizing: border-box;",
                    expiryMonth: "width: calc(50% - 4px); font-size: 1rem; color: #334155;background: #ffffff;padding: 0.75rem 0.75rem;border: 1px solid #cbd5e1;border-radius: 6px;margin-bottom: 1rem;    box-sizing: border-box;",
                    expirySeparator: "display: none",
                    expiryYear: "width: calc(50% - 4px); font-size: 1rem; color: #334155;background: #ffffff;padding: 0.75rem 0.75rem;border: 1px solid #cbd5e1;border-radius: 6px;margin-bottom: 1rem;    box-sizing: border-box;",
                    cvv2: "width: 50%; font-size: 1rem; color: #334155;background: #ffffff;padding: 0.75rem 0.75rem;border: 1px solid #cbd5e1;border-radius: 6px;margin-bottom: 1rem;    box-sizing: border-box;"
                });
                resolve(cardForm);
            }, 0);
        });
    }

    getRelativeTime(isoString: string): string {
        const buildDate = new Date(isoString);
        const now = new Date();
        const diffMs = now.getTime() - buildDate.getTime();

        const minutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const years = Math.floor(days / 365);

        if (years >= 1) {
            const remainingDays = days - years * 365;
            return `${years} year${years !== 1 ? 's' : ''}, ${remainingDays} day${remainingDays !== 1 ? 's' : ''} ago`;
        } else if (days >= 1) {
            const remainingHours = hours - days * 24;
            return `${days} day${days !== 1 ? 's' : ''}, ${remainingHours} hour${remainingHours !== 1 ? 's' : ''} ago`;
        } else {
            const remainingMinutes = minutes - hours * 60;
            return `${hours} hour${hours !== 1 ? 's' : ''}, ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} ago`;
        }
    }
    
    getLocalTimeTooltip(isoString: string): string {
        const buildDate = new Date(isoString);
        // Unstamped dev builds pass a non-date value (e.g. 'dev build'); avoid "Invalid Date".
        if (isNaN(buildDate.getTime())) {
            return 'Development build (not stamped)';
        }
        const localTime = buildDate.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit'
        });
        const relativeTime = this.getRelativeTime(isoString);
        return `Local: ${localTime}\n${relativeTime}`;
    }
}